import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Navbar1 } from "@/components/ui/navbar-1";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  amIAdmin,
  listAllPosts,
  generatePost,
  updatePost,
  deletePost,
  seedInitialPosts,
  type BlogPost,
} from "@/lib/blog.functions";
import { CATEGORIES, CATEGORY_LABELS, type BlogCategory } from "@/lib/blog-topics";

export const Route = createFileRoute("/_authenticated/admin/blog")({
  component: AdminBlog,
});

function AdminBlog() {
  const qc = useQueryClient();
  const isAdminFn = useServerFn(amIAdmin);
  const listFn = useServerFn(listAllPosts);
  const genFn = useServerFn(generatePost);
  const seedFn = useServerFn(seedInitialPosts);
  const updateFn = useServerFn(updatePost);
  const deleteFn = useServerFn(deletePost);

  const { data: isAdmin, isLoading: checking } = useQuery({
    queryKey: ["blog", "is-admin"],
    queryFn: () => isAdminFn(),
  });

  const { data: posts = [], refetch } = useQuery({
    queryKey: ["blog", "admin", "list"],
    queryFn: () => listFn(),
    enabled: !!isAdmin,
  });

  const [category, setCategory] = useState<BlogCategory | "">("");
  const [topic, setTopic] = useState("");
  const [publish, setPublish] = useState(true);

  const genMut = useMutation({
    mutationFn: () =>
      genFn({
        data: {
          category: category || undefined,
          topic: topic.trim() || undefined,
          publish,
        },
      }),
    onSuccess: () => {
      toast.success("Artigo gerado");
      setTopic("");
      qc.invalidateQueries({ queryKey: ["blog"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao gerar"),
  });

  const seedMut = useMutation({
    mutationFn: () => seedFn(),
    onSuccess: () => {
      toast.success("Seed iniciado — pode levar alguns minutos");
      qc.invalidateQueries({ queryKey: ["blog"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Erro ao rodar seed"),
  });

  if (checking) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar1 />
        <div className="mx-auto max-w-4xl px-6 pt-28 text-sm text-muted-foreground">
          Carregando…
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar1 />
        <div className="mx-auto max-w-4xl px-6 pt-28 text-sm text-muted-foreground">
          Acesso restrito.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar1 />
      <main className="mx-auto max-w-5xl px-6 pt-28 pb-16">
        <header className="mb-8 flex items-center justify-between">
          <div>
            <p className="mb-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              Admin · Blog
            </p>
            <h1 className="text-3xl font-medium tracking-tight">Gerador de artigos</h1>
          </div>
          <Link to="/blog" className="text-sm text-muted-foreground underline">
            Ver blog público
          </Link>
        </header>

        <Card className="mb-10 border-white/10 bg-white/[0.02]">
          <CardContent className="space-y-4 p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-2 text-sm">
                <span className="text-muted-foreground">Categoria (opcional)</span>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as BlogCategory | "")}
                  className="rounded-md border border-white/10 bg-black/40 px-3 py-2 text-sm"
                >
                  <option value="">Rotação automática</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-2 text-sm">
                <span className="text-muted-foreground">Tópico / palavra-chave (opcional)</span>
                <Input
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="Ex: como limpar vinil antigo"
                />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={publish}
                onChange={(e) => setPublish(e.target.checked)}
              />
              Publicar imediatamente
            </label>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => genMut.mutate()}
                disabled={genMut.isPending}
              >
                {genMut.isPending ? "Gerando…" : "Gerar artigo com IA"}
              </Button>
              <Button
                variant="outline"
                onClick={() => seedMut.mutate()}
                disabled={seedMut.isPending}
              >
                {seedMut.isPending ? "Gerando 6…" : "Gerar 6 artigos iniciais"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              A geração usa Lovable AI (~10–40s por artigo incluindo capa). O cron
              publica 1 novo artigo a cada 2 dias.
            </p>
          </CardContent>
        </Card>

        <h2 className="mb-4 text-sm uppercase tracking-[0.18em] text-muted-foreground">
          Artigos ({posts.length})
        </h2>
        <ul className="space-y-3">
          {posts.map((p) => (
            <PostRow
              key={p.id}
              post={p}
              onUpdate={async (patch) => {
                await updateFn({ data: { id: p.id, patch } });
                refetch();
              }}
              onDelete={async () => {
                if (!confirm(`Apagar "${p.title}"?`)) return;
                await deleteFn({ data: { id: p.id } });
                refetch();
              }}
            />
          ))}
          {posts.length === 0 && (
            <li className="rounded-lg border border-dashed border-white/10 px-4 py-8 text-center text-sm text-muted-foreground">
              Nenhum artigo. Rode "Gerar 6 artigos iniciais" para começar.
            </li>
          )}
        </ul>
      </main>
    </div>
  );
}

function PostRow({
  post,
  onUpdate,
  onDelete,
}: {
  post: BlogPost;
  onUpdate: (patch: Partial<BlogPost>) => Promise<void>;
  onDelete: () => void | Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState(post.title);
  const [body, setBody] = useState(post.body_md);
  const [excerpt, setExcerpt] = useState(post.excerpt);

  return (
    <li className="rounded-lg border border-white/10 bg-white/[0.02] p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            <span>{CATEGORY_LABELS[post.category]}</span>
            <span>·</span>
            <span className={post.status === "published" ? "text-emerald-400" : "text-yellow-400"}>
              {post.status}
            </span>
          </div>
          <div className="truncate text-base font-medium">{post.title}</div>
          <div className="truncate text-xs text-muted-foreground">/blog/{post.slug}</div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setExpanded(!expanded)}>
            {expanded ? "Fechar" : "Editar"}
          </Button>
          {post.status === "draft" ? (
            <Button size="sm" onClick={() => onUpdate({ status: "published" })}>
              Publicar
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => onUpdate({ status: "draft" })}>
              Despublicar
            </Button>
          )}
          <Button size="sm" variant="destructive" onClick={onDelete}>
            Apagar
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título" />
          <Textarea
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            rows={2}
            placeholder="Excerpt"
          />
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={16}
            placeholder="Markdown"
            className="font-mono text-xs"
          />
          <Button
            size="sm"
            onClick={() => onUpdate({ title, body_md: body, excerpt })}
          >
            Salvar edição
          </Button>
        </div>
      )}
    </li>
  );
}
